import { useState, useMemo, useContext, useEffect, useCallback, useRef } from "react"
import { Link, NavLink, useLocation } from "react-router-dom"
import { filterRows } from "../helpers"
import { reGetCompaniesUrl } from "../../Config/config"
import SortIcon from "mdi-react/SortIcon"
import SortAscendingIcon from "mdi-react/SortAscendingIcon"
import SortDescendingIcon from "mdi-react/SortDescendingIcon"
import "../TableView.css"
import axios from "axios"
import { UserContext } from "../../Contexts/UserContext"
import NoDataPlaceholder from "../shared/NoDataPlaceholder"
import Loader from "../Loader"
import { TableDataContext } from "../../Contexts/TableDataContext"
import { PeFilterContext } from "../../Contexts/PeFilterContext"
import moment from "moment"
import AddNotePopover from "../shared/AddNotePopover"
import Constants from "../../Constants"
import CustomNavigation from "../../Utils/CustomNavigation"
import { routeNames } from "../../routeSegments"
import OverflowToolTip from "../shared/OverflowToolTip"
import NewLogo from "../NewLogo"
import { getSection } from "../../Utils/utils"
import DeleteItemsFromGroup from "../shared/DeleteItemsFromGroup"
import TwolineOverflowToolTip from "../shared/TwolineOverflowToolTip"
import LZString from "lz-string"

const Table = ({ columns, defualtOrderBy, order, orderBy, menu, sub_type_master_id }) => {
  const {
    rows,
    setRows,
    setTableCompanyId,
    data,
    setData,
    isLoad,
    setIsLoad,
    setIsDataLoad,
    setCurrentFilter,
    currentPage,
    setCurrentPage,
    setAllDealIds,
    globalSearchResult,
    setGlobalSearchResult
  } = useContext(TableDataContext)

  const {
    monthPickerValue,
    setMonthPickerValue,
    setIsMonthPickerChanged,
    refineSearchFilters: contextRefineSearchFilters,
    setRefineSearchFilters: contextSetRefineSearchFilters,
    groupId,
    resetDealsByFilter,
  } = useContext(PeFilterContext)

  const [filters, setFilters] = useState({})
  const [sort, setSort] = useState({ order: order, orderBy: orderBy })
  const [refineSearchFilters, setRefineSearchFilters] = useState(contextRefineSearchFilters || {})
  const [reload, setReload] = useState(false)
  const [isDataReady, setIsDataReady] = useState(false)
  const [urlDates, setUrlDates] = useState(null) // Store URL dates directly

  const abortRef = useRef(null)
  const location = useLocation()
  const { getToken, logout } = useContext(UserContext)

  // Process all URL parameters and set all necessary data in one effect
  useEffect(() => {
    console.log("Processing URL parameters and setting up data...")

    const queryParams = new URLSearchParams(location.search)
    let processedFilters = {} // Start with an empty object for URL filters
    let extractedDates = null

    // Handle 'filters' param
    const compressed = queryParams.get("filters")
    if (compressed) {
      try {
        const decompressed = LZString.decompressFromEncodedURIComponent(compressed)
        const urlFilters = JSON.parse(decompressed)
        processedFilters = urlFilters
        console.log("Processed filters from URL:", urlFilters)
      } catch (error) {
        console.error("Error decompressing or parsing filters:", error)
      }
    }

    // Handle 'gs' param (globalSearchResult)
      const gsCompressed = queryParams.get('gs');
      if (gsCompressed) {
        try {
          const gsDecompressed = LZString.decompressFromEncodedURIComponent(gsCompressed);
          const gsData = JSON.parse(gsDecompressed);
          
          console.log('Retrieved globalSearchResult from URL:', gsData);
          if (gsData && Object.keys(gsData).length > 0) {
            // Set the data in the context
            setGlobalSearchResult(gsData);
            console.log('Set globalSearchResult in context from URL param:', gsData);
            
            // Create a globalSearch entry in refineSearchFilters to trigger the right condition
            const pathKey = location.pathname;
            const updatedFilters = {...refineSearchFilters};
            
            if (!updatedFilters[pathKey]) {
              updatedFilters[pathKey] = {};
            }
            
            updatedFilters[pathKey].globalSearch = ['1']; // Set it to have length 1
            setRefineSearchFilters(updatedFilters);
          }
        } catch (error) {
          console.error('Error decompressing or parsing globalSearchResult:', error);
        }
      }

    // Handle 'date' param - extract and store directly
    const dateParam = queryParams.get("date")
    if (dateParam) {
      console.log("Found date parameter:", dateParam)
      const [startStr, endStr] = dateParam.split(",")
      const startDate = moment(startStr, "YYYY-MM-DD")
      const endDate = moment(endStr, "YYYY-MM-DD")

      if (startDate.isValid() && endDate.isValid()) {
        // Store dates directly for API use
        extractedDates = {
          from_date: startDate.format("YYYY-MM-DD"),
          to_date: endDate.format("YYYY-MM-DD"),
        }
        console.log("Extracted dates from URL:", extractedDates)
        setUrlDates(extractedDates)
      } else {
        console.warn("Invalid date format in query params:", { startStr, endStr })
      }
    }

    // Set local and context refineSearchFilters first
    console.log("Setting refineSearchFilters:", processedFilters)
    setRefineSearchFilters(processedFilters)

    // Reset deals by filter
    resetDealsByFilter()

    // Mark data as ready for API call
    setIsDataReady(true)

    console.log("URL processing complete. Data ready for API call.", {
      extractedDates,
      processedFilters,
    })
  }, [location.search]) // Only depend on location.search

  const isFromGlobalSearch = ()=> globalSearchResult?.length === 0 ? false : true

  const category =
    menu == "re"
      ? "re-investments"
      : menu == "re-exit"
        ? "re-exits"
        : menu == "re-ma"
          ? "other-manda"
          : menu == "re-ipo"
            ? "re-ipo"
            : ""
  const modifiedPath = location.pathname.endsWith('/all')? location.pathname.substring(0, location.pathname.lastIndexOf('/')): location.pathname;
  const tableRender = useCallback(
    (page, column, order, groupId) => {
      // Use URL dates if available, otherwise fall back to monthPickerValue
      let dateRange
      if (urlDates) {
        dateRange = urlDates
        console.log("Using URL dates for API call:", dateRange)
      } else if (monthPickerValue && Array.isArray(monthPickerValue) && monthPickerValue.length === 2) {
        dateRange = {
          from_date: moment(monthPickerValue[0]).format("YYYY-MM-DD"),
          to_date: moment(monthPickerValue[1]).format("YYYY-MM-DD"),
        }
        console.log("Using monthPickerValue for API call:", dateRange)
      } else {
        console.log("No valid dates available for API call, using default or empty.")
        // Provide a default or empty date range if none is found
        dateRange = { from_date: null, to_date: null }
      }

      console.log("Making API call with:", {
        page,
        dateRange,
        column,
        order,
        groupId,
        currentRefineFilters: refineSearchFilters['/re/companies/re-investments'], // Log current filters,
        
      })
      console.log(location.pathname, "location pathname",location.pathname.endsWith('/all')? location.pathname.substring(0, location.pathname.lastIndexOf('/')): location.pathname)

      setIsMonthPickerChanged(0)

      axios(`${reGetCompaniesUrl}`, {
        method: "POST",
        signal: abortRef.current.signal,
        data: {
          category:
            menu == "re"
              ? "re-investments"
              : menu == "re-exit"
                ? "re-exits"
                : menu == "re-ma"
                  ? "other-manda"
                  : menu == "re-ipo"
                    ? "re-ipo"
                    : "",
          from: isFromGlobalSearch() ? "gs" : "",
          date_range: dateRange, 
          order_by: {
            column: column,
            direction: order,
          },
          page: page,
          per_page:200,
          ladvisor_ids:
            category === "angel" || category === "incubation"
              ? []
              : refineSearchFilters[modifiedPath]?.ladvisors?.map((acq) => acq.id),
          tadvisor_ids:
            category === "angel" || category === "incubation"
              ? []
              : refineSearchFilters[modifiedPath]?.tadvisors?.map((acq) => acq.id),
          investor_ids: refineSearchFilters[modifiedPath]?.investor_id?.map((acq) => acq.id),
          company_ids: isFromGlobalSearch()
            ? category === "re-investments" && globalSearchResult?.re_investment_cmp_ids.length !== 0
              ? globalSearchResult?.re_investment_cmp_ids
              : category === "re-exits" && globalSearchResult?.re_exit_cmp_ids?.length !== 0
                ? globalSearchResult?.re_exit_cmp_ids
                : category === "re-ipo" && globalSearchResult?.re_ipo?.length !== 0
                  ? globalSearchResult?.re_ipo
                  : [0]
            : refineSearchFilters[modifiedPath]?.target_company?.map((company) => company.id),
          sector_type: refineSearchFilters[modifiedPath]?.fundSector?.map((option) => option.id),
          region: refineSearchFilters[modifiedPath]?.region?.map((region) => region.id),
          city: refineSearchFilters[modifiedPath]?.city?.map((city) => city.id),
          type: Number.parseInt(refineSearchFilters[modifiedPath]?.type?.map((type) => type.id)),
          company_type: refineSearchFilters[modifiedPath]?.companyType?.map((type) => type.id),
          investor_type: refineSearchFilters[modifiedPath]?.investorType?.map((inv) => inv.id),
          deal_range: refineSearchFilters[modifiedPath]?.dealRange?.at(0) && {
            from: Number.parseInt(refineSearchFilters[modifiedPath]?.dealRange[0].name),
            to: Number.parseInt(refineSearchFilters[modifiedPath]?.dealRange[1].name),
          },
          sub_sector: refineSearchFilters[modifiedPath]?.subSector?.map((sector) => sector.id),
          exit_status: refineSearchFilters[modifiedPath]?.exitStatus?.map((status) => status.id),
          group: {
            group_id: groupId?.myList?.map((id) => id.group_id)?.toString() ?? null,
            sub_type_master_id: groupId?.myList?.length > 0 ? sub_type_master_id : null,
          },
        },
        headers: {
          Authorization: getToken(),
        },
      })
        .then((response) => {
          if (response?.data?.message == "Success") {
            setCurrentFilter(response.config.data)
            setIsLoad(false)
            setIsDataLoad(false)
            setIsMonthPickerChanged(0)
            setRows([])
            setRows(response?.data?.companies)
            setTableCompanyId([])
            setTableCompanyId(response?.data?.all_companies_ids)
            setAllDealIds(response?.data?.all_deal_ids)
            setData(response?.data)
            console.log("API call successful, data loaded")
          }
        })
        .catch((error) => {
          if (
            error?.response?.status == Constants.sessionTerminatedStatus ||
            error?.response?.status == Constants.InvalidTokenStatus
          ) {
            logout()
          }
          console.log("API not working", error)
        })
    },
    [refineSearchFilters, location.pathname, category, globalSearchResult, menu, urlDates, monthPickerValue, groupId],
  ) // Added groupId to dependencies

  // Single effect that calls tableRender only once after all data is ready
  useEffect(() => {
    if (!isDataReady) {
      console.log("Data not ready yet, skipping initial API call")
      return
    }

    // Check if we have either URL dates or monthPickerValue
    const hasValidDates =
      urlDates || (monthPickerValue && Array.isArray(monthPickerValue) && monthPickerValue.length === 2)

    if (!hasValidDates) {
      console.log("No valid dates available for initial API call, proceeding with current state.")
      // We still want to make the call even if dates are missing,
      // as the backend might have default behavior or other filters apply.
    }

    console.log("All data ready, making single initial API call...")

    const abortController = new AbortController()
    abortRef.current = abortController
    setIsLoad(true)
    setIsDataLoad(true)

    // Call tableRender only once with all the processed data
    tableRender(currentPage, sort.orderBy, sort.order, groupId)

    return () => {
      abortController.abort()
    }
  }, [isDataReady, urlDates, monthPickerValue, groupId, reload, tableRender, currentPage, sort])


  const findActivePage = (page) => {
    setCurrentPage(page)
    tableRender(page, sort.orderBy, sort.order, groupId)
    setIsLoad(true)
  }

  const handleSort = (accessor) => {
    setCurrentPage(currentPage)
    setRows([])
    setIsLoad(true)
    setIsDataLoad(true)
    setSort((prevSort) => ({
      order: prevSort.orderBy === accessor && prevSort.order === "desc" ? "asc" : "desc",
      orderBy: accessor,
    }))
    const newOrder = sort.orderBy === accessor && sort.order === "desc" ? "asc" : "desc"
    tableRender(currentPage, accessor, newOrder, groupId)
  }

  const filteredRows = useMemo(() => filterRows(rows, filters), [rows, filters])
  const count = filteredRows?.length

  const addCommasToNumber = (number) => {
    return number?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  return (
    <>
      <div className="md:col-span-4 col-span-5">
        <div className="lg:col-span-4">
          <div className="scrollbar ">
            <div
              className={
                "overflow-x-auto scrollbar rounded rounded-[6px] rounded-t-none shadow-[0_2px_6px_0_rgba(0,0,0,0.08)] " +
                (rows?.length > 5 ? "" : "pr-2")
              }
            >
              <table className=" listPage table w-full p-1 bg-grey-50 divide-y">
                <thead className="bg-[#EBEBEB] text-[11pt] leading-[16pt] lg:text-[13px] lg:leading-[15px] font-sans_semi_bold text-[#666666]  text-left">
                  <tr>
                    {columns.map((column, i) => {
                      const sortIcon = () => {
                        if (column.accessor === sort.orderBy) {
                          if (sort.order === "asc") {
                            return <SortDescendingIcon className="pl-2 block h-[15px]" />
                          }
                          return <SortAscendingIcon className="pl-2 block h-[15px]" />
                        } else {
                          return <SortIcon className="pl-2 block h-[15px] " />
                        }
                      }
                      return (
                        <th className="title px-5 py-2 font-sans_book_body" key={i}>
                          <span className="flex">
                            <OverflowToolTip
                              component={
                                <a className="cursor-text hover:text-[#000000]">
                                  {column.accessor === "add" && groupId?.myList?.length > 0 ? (
                                    "Delete"
                                  ) : column.accessor === "add" ? (
                                    <div className="flex flex-col text-center">
                                      <span>Create</span>
                                      <span>your list</span>
                                    </div>
                                  ) : column.accessor === "amount" ||
                                    column.accessor === "amount_m" ||
                                    column.accessor === "deal_amount" ? (
                                    <div className="flex flex-col text-right">
                                      <span>Amount</span>
                                      <span>(US$ M)</span>
                                    </div>
                                  ) : column.accessor === "ipo_size_m" ? (
                                    <div className="flex flex-col text-right">
                                      <span>Deal Size</span>
                                      <span>(US$ M)</span>
                                    </div>
                                  ) : column.label ? (
                                    column.label
                                  ) : (
                                    "--"
                                  )}
                                </a>
                              }
                            />
                            {column.accessor !== "add" && (
                              <button onClick={() => handleSort(column.accessor)}>{sortIcon()}</button>
                            )}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                {!isLoad ? (
                  count > 0 ? (
                    <tbody className="text-[13pt] leading-[16pt] lg:text-[14px] lg:leading-[16px] text-[#333333] text-left font-sans_book_body">
                      {rows.length > 0 ? (
                        menu == "re" ? (
                          rows?.map((row, i) => {
                            return (
                              <tr
                                className=" group hover:border-l-4 hover:border-l-[#BC8B20] border-l-4 border-l-[#FFFFFF]  border-b bg-white text"
                                key={i}
                              >
                                <td className="md:w-[23%] pl-5 leading-[2rem] text-left sticky left-0 lg:relative text-[#333333] bg-white py-3 font-sans_book_body items-center justify-center">
                                  <NewLogo id={row.company_id} name={row.company} menu={"re"} />
                                  <div className="whitespace-normal break-words">
                                    <OverflowToolTip
                                      className="w-[10rem] md:w-[10rem] lg:w-auto lg:max-w-[13rem] 2xl:max-w-[30rem] flex flex-row gap-2"
                                      component={
                                        <NavLink
                                          className="flex text-start leading-[1.8rem] lg:inline whitespace-normal break-words hover:text-[#BC8B20] font-medium"
                                          state={{ prevPath: location.pathname }}
                                          to={{
                                            pathname: routeNames.reCompanyById(row.company_id),
                                            search: getSection(Constants.primaryMasterSubIds.re_investments_id),
                                          }}
                                        >
                                          {row?.company ? row.company : "--"}
                                        </NavLink>
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[10%]">
                                  <a>{row?.city ? row.city : "--"}</a>
                                </td>
                                <td className="whitespace-normal break-words px-5 py-3 font-[Squel Sans] w-[18%] font-medium">
                                  <div className="whitespace-normal break-words">
                                    <TwolineOverflowToolTip
                                      className="w-[10rem] lg:max-w-[17rem] 2xl:max-w-[30rem] line-clamp-2 leading-relaxed"
                                      component={
                                        <Link
                                          rel="noopener noreferrer"
                                          state={{ prevPath: location.pathname }}
                                          to={routeNames.reInvestorById(row.investor_id)}
                                          className="text-start hover:text-[#000000] cursor-text whitespace-normal break-words"
                                        >
                                          {row?.investor?.length > 0
                                            ? row?.investor?.map((inve, i) => (
                                                <span key={inve.investor_id}>
                                                  <CustomNavigation
                                                    mainModule={menu == "re" ? "re" : ""}
                                                    module="investor"
                                                    name={
                                                      row?.investor?.length - 1 === i
                                                        ? inve.investor_name
                                                        : inve.investor_name + ", "
                                                    }
                                                    entityId={inve?.investor_id}
                                                    type={inve?.investor_type}
                                                    stake={inve?.stake}
                                                  />
                                                </span>
                                              ))
                                            : "--"}
                                        </Link>
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[12%]">
                                  {row?.deal_date ? (
                                    <Link
                                      className="hover:text-[#BC8B20] font-medium"
                                      to={{
                                        pathname: routeNames.reCompanyInvestmentDealById(row?.company_id, row?.id),
                                      }}
                                    >
                                      {row.deal_date}
                                    </Link>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[10%]">
                                  <a>{row?.exit_status ? row.exit_status : "--"}</a>
                                </td>
                                <td className="whitespace-nowrap   px-5 py-3 font-[Squel Sans] w-[2%]  text-center pr-[3rem] md:pr-[2rem] lg:pr-[3rem] xl:pr-[3rem] 2xl:pr-[3rem] 3xl:pr-[4rem]">
                                  <a>{row?.deal_amount == 0 || row?.deal_amount == 0.0 ? "--" : row.deal_amount}</a>
                                </td>
                                <td className="whitespace-nowrap   py-3 font-[Squel Sans] w-[1%]">
                                  {groupId?.myList?.length > 0 ? (
                                    <DeleteItemsFromGroup
                                      id={row.company_id}
                                      group_id={row?.grp_lt_item_id}
                                      type_id={1}
                                      setReload={setReload}
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <AddNotePopover
                                        key={row.company_id}
                                        companyId={row.company_id}
                                        companyName={row.company}
                                        type_id={1}
                                        sub_type_master_id={sub_type_master_id}
                                      />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        ) : menu == "re-exit" ? (
                          rows?.map((row, i) => {
                            return (
                              <tr
                                className=" group hover:border-l-4 hover:border-l-[#BC8B20] border-l-4 border-l-[#FFFFFF]  border-b bg-white text"
                                key={i}
                              >
                                <td className="md:w-[23%] pl-5 leading-[2rem] text-left sticky left-0 lg:relative text-[#333333] bg-white py-3 font-sans_book_body items-center justify-center">
                                  <NewLogo id={row.company_id} name={row.company} menu={"re"} />
                                  <div className="whitespace-normal break-words">
                                    <OverflowToolTip
                                      className="w-[10rem] md:w-[10rem] lg:w-auto lg:max-w-[13rem] 2xl:max-w-[30rem] flex flex-row gap-2"
                                      component={
                                        <NavLink
                                          className="flex text-start leading-[1.8rem] lg:inline whitespace-normal break-words hover:text-[#BC8B20] font-medium"
                                          state={{ prevPath: location.pathname }}
                                          to={{
                                            pathname: routeNames.reCompanyById(row.company_id),
                                            search: getSection(Constants.primaryMasterSubIds.re_exits_id),
                                          }}
                                        >
                                          {row?.company ? row.company : "--"}
                                        </NavLink>
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[15%]">
                                  <a>{row?.city ? row.city : "--"}</a>
                                </td>
                                <td className="whitespace-normal break-words px-5 py-3 font-[Squel Sans] w-[21%]">
                                  <div className="whitespace-normal break-words">
                                    <TwolineOverflowToolTip
                                      className="w-[10rem] lg:max-w-[17rem] 2xl:max-w-[30rem] line-clamp-2 leading-relaxed"
                                      component={
                                        <Link
                                          rel="noopener noreferrer"
                                          className="flex text-start lg:inline lg:whitespace-normal hover:text-[#BC8B20] font-medium"
                                          state={{ prevPath: location.pathname }}
                                          to={routeNames.reInvestorById(row.investor_id)}
                                        >
                                          {row?.investor?.length > 0
                                            ? row?.investor?.map((inv, i) => (
                                                <span key={inv.investor_id}>
                                                  <CustomNavigation
                                                    mainModule={menu === "re-exit" ? "re" : ""}
                                                    module="investor"
                                                    name={
                                                      row?.investor?.length - 1 === i
                                                        ? inv.investor_name
                                                        : inv.investor_name + ", "
                                                    }
                                                    entityId={inv?.investor_id}
                                                    type={inv?.investor_type}
                                                    stake={inv?.stake}
                                                  />
                                                </span>
                                              ))
                                            : "--"}
                                        </Link>
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[12%]">
                                  {row?.deal_date ? (
                                    <Link
                                      className="hover:text-[#BC8B20] font-medium"
                                      to={{
                                        pathname: routeNames.reCompanyExitsDealById(row?.company_id, row?.id),
                                      }}
                                    >
                                      {row.deal_date}
                                    </Link>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[10%]">
                                  <a>{row?.exit_status ? row.exit_status : "--"}</a>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[9%] text-center  pr-[3rem] md:pr-[2rem] lg:pr-[2.5rem]">
                                  <a>
                                    {row?.deal_amount != 0 || row?.deal_amount != 0.0
                                      ? addCommasToNumber(row.deal_amount)
                                      : "--"}
                                  </a>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[3%]">
                                  {groupId?.myList?.length > 0 ? (
                                    <DeleteItemsFromGroup
                                      id={row.company_id}
                                      group_id={row?.grp_lt_item_id}
                                      type_id={1}
                                      setReload={setReload}
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <AddNotePopover
                                        key={row.company_id}
                                        companyId={row.company_id}
                                        companyName={row.company}
                                        type_id={1}
                                        sub_type_master_id={sub_type_master_id}
                                      />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        ) : menu == "re-ma" ? (
                          rows?.map((row, i) => {
                            return (
                              <tr
                                className=" group hover:border-l-4 hover:border-l-[#BC8B20] border-l-4 border-l-[#FFFFFF]  border-b bg-white text"
                                key={i}
                              >
                                <td className="md:w-[13%] pl-5 leading-[2rem] text-left sticky left-0 lg:relative text-[#333333] bg-white py-3 font-sans_book_body items-center justify-center">
                                  <NewLogo id={row.company_id} name={row.company} menu={"re"} />
                                  <div className="whitespace-normal break-words">
                                    <OverflowToolTip
                                      className="w-[10rem] md:w-[10rem] lg:w-auto lg:max-w-[13rem] 2xl:max-w-[30rem] flex flex-row gap-2"
                                      component={
                                        <NavLink
                                          className="flex text-start leading-[1.8rem] lg:inline whitespace-normal break-words hover:text-[#BC8B20] font-medium"
                                          state={{ prevPath: location.pathname }}
                                          to={{
                                            pathname: routeNames.reCompanyById(row.company_id),
                                            search: getSection(Constants.primaryMasterSubIds.re_others_ma_id),
                                          }}
                                        >
                                          {row?.company ? row.company : "--"}
                                        </NavLink>
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[6%]">
                                  <a>{row?.city ? row.city : "--"}</a>
                                </td>
                                <td className="whitespace-normal break-words px-5 py-3 font-[Squel Sans] w-[17%]">
                                  {row?.acquirer ? (
                                    <TwolineOverflowToolTip
                                      className="w-[10rem] line-clamp-2 leading-relaxed"
                                      component={<span className="whitespace-normal break-words">{row?.acquirer}</span>}
                                    />
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[4%]">
                                  {row?.deal_date ? (
                                    <Link
                                      className="hover:text-[#BC8B20] font-medium"
                                      to={{
                                        pathname: routeNames.reCompanyOtherMaDealById(row?.company_id, row?.id),
                                      }}
                                    >
                                      {row.deal_date}
                                    </Link>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[3%] text-center  pr-[2.8rem]">
                                  <a>
                                    {row?.deal_amount != 0 || row?.deal_amount != 0.0
                                      ? addCommasToNumber(row.deal_amount)
                                      : "--"}
                                  </a>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[1%]">
                                  {groupId?.myList?.length > 0 ? (
                                    <DeleteItemsFromGroup
                                      id={row.company_id}
                                      group_id={row?.grp_lt_item_id}
                                      type_id={1}
                                      setReload={setReload}
                                    />
                                  ) : (
                                    <AddNotePopover
                                      key={row.company_id}
                                      companyId={row.company_id}
                                      companyName={row.company}
                                      type_id={1}
                                      sub_type_master_id={sub_type_master_id}
                                    />
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        ) : menu == "re-ipo" ? (
                          rows?.map((row, i) => {
                            return (
                              <tr
                                className=" group hover:border-l-4 hover:border-l-[#BC8B20] border-l-4 border-l-[#FFFFFF]  border-b bg-white text"
                                key={i}
                              >
                                <td className="md:w-[25%] pl-5 leading-[2rem] text-left sticky left-0 lg:relative text-[#333333] bg-white py-3 font-sans_book_body items-center justify-center">
                                  <NewLogo id={row.company_id} name={row.company} menu={"re"} />
                                  <div className="whitespace-normal break-words">
                                    <OverflowToolTip
                                      className="w-[10rem] md:w-[10rem] lg:w-auto lg:max-w-[13rem] 2xl:max-w-[30rem] flex flex-row gap-2"
                                      component={
                                        <NavLink
                                          className="flex text-start leading-[1.8rem] lg:inline whitespace-normal break-words hover:text-[#BC8B20] font-medium"
                                          state={{ prevPath: location.pathname }}
                                          to={{
                                            pathname: routeNames.reCompanyById(row.company_id),
                                            search: getSection(Constants.primaryMasterSubIds.re_backed_ipos_id),
                                          }}
                                        >
                                          {row?.company ? row.company : "--"}
                                        </NavLink>
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[12%]">
                                  <a>{row?.city ? row.city : "--"}</a>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[11%]">
                                  <OverflowToolTip
                                    className="w-[10rem]"
                                    component={<a>{row?.sector ? row.sector : "--"}</a>}
                                  />
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[4%] ">
                                  {row?.date ? (
                                    <Link
                                      className="hover:text-[#BC8B20] font-medium"
                                      to={{
                                        pathname: routeNames.reCompanyBackedIPOSDealById(row?.company_id, row?.id),
                                      }}
                                    >
                                      {row.date}
                                    </Link>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td className="whitespace-nowrap  py-3 font-[Squel Sans] text-right pr-[3rem] lg:pr-[3rem] 3xl:pr-[3.5rem] w-[6%]">
                                  <a>{row?.ipo_size != 0 ? addCommasToNumber(row.ipo_size) : "--"}</a>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[7%]">
                                  <a>{row?.exit_status ? row.exit_status : "--"}</a>
                                </td>
                                <td className="whitespace-nowrap  px-5 py-3 font-[Squel Sans] w-[1%]">
                                  {groupId?.myList?.length > 0 ? (
                                    <DeleteItemsFromGroup
                                      id={row.company_id}
                                      group_id={row?.grp_lt_item_id}
                                      type_id={1}
                                      setReload={setReload}
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <AddNotePopover
                                        key={row.company_id}
                                        companyId={row.company_id}
                                        companyName={row.company}
                                        type_id={1}
                                        sub_type_master_id={sub_type_master_id}
                                      />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        ) : (
                          ""
                        )
                      ) : (
                        <center>
                          <div className="h-[4rem] text-[12pt] lg:text-[15px] text-[#333333] relative mt-[3rem] left-[30rem]">
                            No data found
                          </div>
                        </center>
                      )}
                    </tbody>
                  ) : (
                    <></>
                  )
                ) : (
                  <></>
                )}
              </table>
              {!isLoad ? (
                count > 0 ? (
                  <></>
                ) : (
                  <div className="pr-2">
                    <div className="w-full h-[17rem] bg-white flex items-center justify-center fixed md:relative">
                      <NoDataPlaceholder />
                    </div>
                  </div>
                )
              ) : (
                <div className="w-full h-[17rem] bg-white flex items-center justify-center absolute md:relative">
                  <Loader />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Table
